// src/pages/admin/ActiveMember.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  Filter, 
  UserCheck, 
  Phone, 
  Calendar, 
  Eye, 
  Mail,
  MoreVertical,
  Download,
  User,
  Shield,
  DollarSign,
  Users,
  Clock,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Power,
  Edit,
  FileText,
  Loader2,
  CreditCard,
  ArrowUpDown,
  BarChart3
} from 'lucide-react';
import { database, ref, onValue } from '../../firebaseConfig';

const ActiveMember = () => {
  const [activeMembers, setActiveMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    memberId: '',
    mobile: '',
    joiningDateFrom: '',
    joiningDateTo: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'joiningDate', direction: 'desc' });
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedMobileMember, setExpandedMobileMember] = useState(null);
  const [showPassword, setShowPassword] = useState({});
  const [actionMenu, setActionMenu] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const menuRef = useRef();

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch data from Firebase - Updated for nested date structure
  useEffect(() => {
    const usersRef = ref(database, 'users');
    onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      let userList = [];
      let totalCount = 0;
      
      if (data) {
        // Iterate through each date (e.g., 2026-02-06)
        Object.keys(data).forEach(dateKey => {
          const dateData = data[dateKey];
          
          // Check if dateData is an object (not null or undefined)
          if (dateData && typeof dateData === 'object') {
            // Iterate through users under this date
            Object.keys(dateData).forEach(userKey => {
              const user = dateData[userKey];
              
              // Skip admin users or invalid entries
              if (!user || userKey === 'admin' || user.userId === 'admin' || user.role === 'admin') {
                return;
              }
              
              totalCount++; // Count all non-admin users
              
              // Only include active users
              if (user.status === 'active') {
                userList.push({
                  id: `${dateKey}_${userKey}`, // Unique ID combining date and user key
                  firebaseDateKey: dateKey,
                  firebaseUserKey: userKey,
                  ...user
                });
              }
            });
          }
        });
      }
      
      console.log(`Found ${userList.length} active users out of ${totalCount} total users`);
      setAllUsers(userList);
      setTotalMembers(totalCount);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching users:", error);
      setLoading(false);
    });
  }, []);

  // Process users
  useEffect(() => {
    if (loading) return;

// Replace the user processing logic in the useEffect
const activeUsers = allUsers
  .filter(user => {
    // Check if user has an active package
    if (user.packages && Array.isArray(user.packages)) {
      return user.packages.some(pkg => pkg.status === 'active');
    }
    // Or if they have a package field (single package)
    if (user.package && user.package.status === 'active') {
      return true;
    }
    return false;
  })
  .map(user => {
    // Find the active package
    let activePackage = null;
    if (user.packages && Array.isArray(user.packages)) {
      activePackage = user.packages.find(pkg => pkg.status === 'active');
    } else if (user.package && user.package.status === 'active') {
      activePackage = user.package;
    }
    
    const parentId = user.uplineSponsorId || user.sponsorId || 'N/A';
    const sponsorId = user.sponsorId || user.sponserId || 'N/A';
    
    // Use package activation date if available, otherwise registration date
    const joinDate = activePackage?.activationDate || 
                    user.registrationDate || 
                    user.joiningDate || 
                    user.createdAt || 
                    user.firebaseDateKey ? 
      new Date(activePackage?.activationDate || user.registrationDate || user.joiningDate || user.createdAt || user.firebaseDateKey) : new Date();
    
    const lastLogin = user.lastLogin ? new Date(user.lastLogin) : joinDate;
    const daysSinceActivation = activePackage ? Math.max(0, Math.floor((new Date() - new Date(activePackage.activationDate)) / (1000 * 60 * 60 * 24))) : 0;
    
    const balance = user.fund?.balance || user.balance || 0;
    
    let memberId = user.memberId || user.userId || user.firebaseUserKey;
    if (!memberId.startsWith('AURUM') && !memberId.startsWith('AV')) {
      memberId = `AV${String(user.firebaseUserKey).substring(0, 6)}`;
    }
    
    return {
      id: user.id,
      userId: user.userId || user.firebaseUserKey,
      memberId: memberId,
      fullName: user.name || user.fullName || 'Unknown User',
      parentId: parentId,
      sponsorId: sponsorId,
      password: user.password || '••••••••',
      mobile: user.mobile || user.phone || 'N/A',
      email: user.email || user.userId || `${user.firebaseUserKey}@example.com`,
      joiningDate: joinDate,
      activationDate: activePackage?.activationDate || null,
      lastLogin: lastLogin,
      balance: parseFloat(balance),
      totalEarnings: parseFloat(user.totalEarnings || user.earnings || 0),
      referralCount: user.referralCount || user.referrals || 0,
      country: user.country || 'N/A',
      deposited: parseFloat(user.deposited || user.totalDeposit || 0),
      withdrawal: parseFloat(user.withdrawal || user.totalWithdraw || 0),
      status: user.status || 'active',
      daysActive: daysSinceActivation,
      packageName: activePackage?.packageName || activePackage?.packagePlan || 'Active Package',
      packageAmount: activePackage?.investmentAmount || activePackage?.baseAmount || 0,
      transactionId: activePackage?.transactionId || '',
      role: user.role || 'member',
      firebaseDateKey: user.firebaseDateKey,
      firebaseUserKey: user.firebaseUserKey
    };
  });

    // Sort users
    const sortedUsers = [...activeUsers].sort((a, b) => {
      if (sortConfig.key === 'joiningDate') {
        return sortConfig.direction === 'asc' 
          ? a.joiningDate - b.joiningDate 
          : b.joiningDate - a.joiningDate;
      }
      if (sortConfig.key === 'balance') {
        return sortConfig.direction === 'asc' 
          ? a.balance - b.balance 
          : b.balance - a.balance;
      }
      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'asc'
          ? a.fullName.localeCompare(b.fullName)
          : b.fullName.localeCompare(a.fullName);
      }
      return 0;
    });

    console.log(`Processed ${sortedUsers.length} active members for display`);
    setActiveMembers(sortedUsers);
  }, [allUsers, loading, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const filteredMembers = activeMembers.filter(member => {
    const matchesMemberId = !filters.memberId || 
      (member.memberId && member.memberId.toLowerCase().includes(filters.memberId.toLowerCase())) ||
      (member.userId && member.userId.toLowerCase().includes(filters.memberId.toLowerCase()));
    
    const matchesMobile = !filters.mobile || 
      (member.mobile && member.mobile.includes(filters.mobile));
    
    let matchesJoiningDate = true;
    if (filters.joiningDateFrom || filters.joiningDateTo) {
      const joiningDate = member.joiningDate;
      if (filters.joiningDateFrom) {
        const fromDate = new Date(filters.joiningDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        matchesJoiningDate = matchesJoiningDate && joiningDate >= fromDate;
      }
      if (filters.joiningDateTo) {
        const toDate = new Date(filters.joiningDateTo);
        toDate.setHours(23, 59, 59, 999);
        matchesJoiningDate = matchesJoiningDate && joiningDate <= toDate;
      }
    }
    return matchesMemberId && matchesMobile && matchesJoiningDate;
  });

  const togglePasswordVisibility = (id, e) => {
    e.stopPropagation();
    setShowPassword(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleRowExpand = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const toggleMobileMember = (id) => {
    setExpandedMobileMember(expandedMobileMember === id ? null : id);
  };

  const toggleActionMenu = (id, e) => {
    e.stopPropagation();
    setActionMenu(actionMenu === id ? null : id);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return 'N/A';
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return 'N/A';
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const exportToCSV = () => {
    const headers = [
      'S.No', 'Sponsor ID', 'Member ID', 'Full Name', 'Status', 'Joining Date', 'Days Active',
      'Balance', 'Total Earnings', 'Mobile', 'Email', 'Referrals',
      'Deposited', 'Withdrawal', 'Country'
    ];
    
    const csvData = filteredMembers.map((member, index) => [
      index + 1,
      member.sponsorId || 'N/A',
      member.memberId || 'N/A',
      member.fullName || 'N/A',
      member.status || 'active',
      formatDate(member.joiningDate),
      member.daysActive || 0,
      member.balance || 0,
      member.totalEarnings || 0,
      member.mobile || 'N/A',
      member.email || 'N/A',
      member.referralCount || 0,
      member.deposited || 0,
      member.withdrawal || 0,
      member.country || 'N/A'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `active-members-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const calculateTotalEarnings = () => {
    return activeMembers.reduce((total, member) => total + (member.totalEarnings || 0), 0);
  };

  const calculateTodayLogins = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return activeMembers.filter(member => {
      if (!member.lastLogin) return false;
      const lastLogin = new Date(member.lastLogin);
      lastLogin.setHours(0, 0, 0, 0);
      return lastLogin.getTime() === today.getTime();
    }).length;
  };

  const handleViewProfile = (member) => {
    setSelectedMember(member);
    setShowProfileDialog(true);
    setActionMenu(null);
  };

  const ProfileDialog = () => {
    if (!selectedMember) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-secondary border border-border rounded-lg w-full max-w-md">
          <div className="p-4 border-b border-border flex justify-between items-center">
            <h3 className="text-lg font-bold text-foreground font-heading">
              Member Profile
            </h3>
            <button
              onClick={() => setShowProfileDialog(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              ✕
            </button>
          </div>
          
          <div className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <User className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <h4 className="font-bold text-foreground font-heading">{selectedMember.fullName}</h4>
                <p className="text-muted-foreground text-sm font-body">{selectedMember.memberId}</p>
                <p className="text-xs text-muted-foreground font-body">Status: {selectedMember.status}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-secondary/50 p-3 rounded-lg">
                <div className="text-xs text-muted-foreground font-body mb-1">Balance</div>
                <div className="text-green-400 font-bold font-heading text-lg">
                  {formatCurrency(selectedMember.balance)}
                </div>
              </div>
              <div className="bg-secondary/50 p-3 rounded-lg">
                <div className="text-xs text-muted-foreground font-body mb-1">Earnings</div>
                <div className="text-foreground font-bold font-heading text-lg">
                  {formatCurrency(selectedMember.totalEarnings)}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground font-body">Sponsor ID:</span>
                <span className="text-sm text-foreground font-body">{selectedMember.sponsorId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground font-body">Status:</span>
                <span className="text-sm text-green-500 font-body">{selectedMember.status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground font-body">Joined:</span>
                <span className="text-sm text-foreground font-body">{formatDate(selectedMember.joiningDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground font-body">Last Login:</span>
                <span className="text-sm text-foreground font-body">{formatDate(selectedMember.lastLogin)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground font-body">Mobile:</span>
                <span className="text-sm text-foreground font-body">{selectedMember.mobile}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground font-body">Email:</span>
                <span className="text-sm text-foreground font-body">{selectedMember.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground font-body">Referrals:</span>
                <span className="text-sm text-foreground font-body">{selectedMember.referralCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground font-body">Country:</span>
                <span className="text-sm text-foreground font-body">{selectedMember.country}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground font-body">Password:</span>
                <span className="text-sm text-foreground font-mono">
                  {showPassword[selectedMember.id] ? selectedMember.password : '••••••••'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground font-body">Active Days:</span>
                {/* <span className="text-sm text-green-500 font-body">{selectedMember.daysActive} days</span> */}
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-border flex gap-2">
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  `Name: ${selectedMember.fullName}\n` +
                  `Member ID: ${selectedMember.memberId}\n` +
                  `Sponsor ID: ${selectedMember.sponsorId}\n` +
                  `Balance: ${formatCurrency(selectedMember.balance)}\n` +
                  `Mobile: ${selectedMember.mobile}\n` +
                  `Email: ${selectedMember.email}\n` +
                  `Status: ${selectedMember.status}\n` +
                  `Active Days: ${selectedMember.daysActive}`
                );
                alert('Details copied to clipboard!');
              }}
              className="flex-1 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors font-body text-sm"
            >
              Copy Details
            </button>
            <button
              onClick={() => setShowProfileDialog(false)}
              className="flex-1 px-4 py-2 bg-secondary hover:bg-secondary/80 text-foreground rounded-lg transition-colors font-body text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleEditUser = (member) => {
    alert(`Edit user: ${member.fullName}\n\nThis will open edit modal in production.`);
  };

  const handleDeactivateUser = (member) => {
    if (window.confirm(`Are you sure you want to deactivate ${member.fullName}?`)) {
      alert(`User ${member.fullName} has been deactivated!\n\nThis would update the user's status to "inactive" in Firebase.`);
      setActionMenu(null);
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="h-3 w-3 opacity-50" />;
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" /> 
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Desktop Table Row Component - Without Actions Column
  const DesktopTableRow = ({ member, index }) => (
    <React.Fragment key={member.id}>
      <tr className="hover:bg-secondary/10 transition-colors">
        <td className="py-2 px-2 text-muted-foreground text-xs text-center font-body">
          {index + 1}
        </td>

        <td className="py-2 px-2">
          <div className="text-foreground text-xs truncate max-w-[80px] font-body">
            {member.sponsorId}
          </div>
        </td>

        <td className="py-2 px-2">
          <div className="flex items-center">
            <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mr-2 flex-shrink-0">
              <User className="h-3 w-3 text-green-500" />
            </div>
            <div className="min-w-0">
              <div className="font-medium text-foreground text-xs truncate font-body">{member.fullName}</div>
              <div className="text-muted-foreground text-[10px] truncate font-body flex items-center">
                <Shield className="h-2.5 w-2.5 mr-1 flex-shrink-0" />
                {member.memberId}
              </div>
            </div>
          </div>
        </td>

        <td className="py-2 px-2">
          <div className="space-y-0.5">
            <div className="text-foreground text-xs font-body flex items-center">
              <TrendingUp className="h-2.5 w-2.5 mr-1 text-green-500" />
              ${(member.totalEarnings || 0).toFixed(2)}
            </div>
            <div className="text-muted-foreground text-xs font-body">
              Ref: <span className="text-blue-500">{member.referralCount}</span>
            </div>
          </div>
        </td>

        <td className="py-2 px-2">
          <div className="space-y-0.5">
            <div className="text-foreground text-xs truncate max-w-[90px] font-body">{member.mobile}</div>
            <div className="text-muted-foreground text-xs truncate max-w-[90px] font-body">{member.email}</div>
          </div>
        </td>

        <td className="py-2 px-2">
          <div className="space-y-0.5">
            <div className="text-foreground text-xs font-body">
              {formatDate(member.joiningDate)}
            </div>
            {/* <div className="text-green-500 text-xs font-medium font-body">
              {member.daysActive} days
            </div> */}
          </div>
        </td>

        <td className="py-2 px-2">
          <div className="text-foreground font-medium text-sm flex items-center font-body">
            <DollarSign className="h-3 w-3 mr-1 text-green-500" />
            {formatCurrency(member.balance)}
          </div>
        </td>

        <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => toggleRowExpand(member.id)}
            className="p-1 hover:bg-secondary/50 rounded transition-colors"
            title="View Details"
          >
            {expandedRow === member.id ? (
              <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </button>
        </td>
      </tr>

      {/* Expanded Details */}
      {expandedRow === member.id && (
        <tr className="bg-secondary/10">
          <td colSpan="8" className="py-3 px-4">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="space-y-1">
                <div className="text-muted-foreground text-xs font-medium font-heading">Credentials</div>
                <div className="flex items-center">
                  <span className="text-foreground font-body mr-2">
                    Pass: {showPassword[member.id] ? member.password : '••••••••'}
                  </span>
                  <button
                    onClick={(e) => togglePasswordVisibility(member.id, e)}
                    className="text-xs text-primary hover:text-primary/80 font-body"
                  >
                    {showPassword[member.id] ? 'Hide' : 'Show'}
                  </button>
                </div>
                <div className="text-muted-foreground font-body">Role: {member.role}</div>
                <div className="text-muted-foreground font-body">Status: {member.status}</div>
              </div>
              <div className="space-y-1">
                <div className="text-muted-foreground text-xs font-medium font-heading">Account Details</div>
                <div className="text-foreground font-body">
                  Sponsor ID: {member.sponsorId}
                </div>
                <div className="text-muted-foreground font-body">
                  Parent ID: {member.parentId}
                </div>
                <div className="text-muted-foreground font-body">
                  User ID: {member.userId}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-muted-foreground text-xs font-medium font-heading">Activity</div>
                <div className="text-foreground font-body">
                  Last Login: {formatDate(member.lastLogin)}
                </div>
                <div className="text-muted-foreground font-body">
                  Country: {member.country}
                </div>
                <div className="text-muted-foreground font-body">
                  Active Days: {member.daysActive}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );

  // Mobile Accordion Item Component
  const MobileAccordionItem = ({ member, index }) => (
    <div key={member.id} className="card-glow mb-4 overflow-hidden">
      <div 
        className="p-4 bg-secondary hover:bg-secondary/80 transition-colors cursor-pointer flex justify-between items-center"
        onClick={() => toggleMobileMember(member.id)}
      >
        <div className="flex items-center gap-3">
          <div className="text-silver font-body text-sm font-mono">#{index + 1}</div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center">
              <User className="h-4 w-4 text-green-500" />
            </div>
            <div>
              <div className="font-bold text-foreground font-heading text-sm">{member.fullName}</div>
              <div className="text-xs text-muted-foreground font-body">
                {member.memberId} | Sponsor: {member.sponsorId}
              </div>
              <div className="text-[10px] text-green-500 font-body">
                Active: {member.daysActive} days
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`px-2 py-1 rounded-full text-xs ${
            member.status === 'active'
              ? 'bg-green-500/20 text-green-400'
              : 'bg-red-500/20 text-red-400'
          }`}>
            {member.status}
          </div>
          <ChevronDown className={`h-5 w-5 text-silver transition-transform ${
            expandedMobileMember === member.id ? 'rotate-180' : ''
          }`} />
        </div>
      </div>
      
      {expandedMobileMember === member.id && (
        <div className="p-4 border-t border-border">
          {/* Financial Info */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-secondary/50 p-3 rounded-lg">
              <div className="text-xs text-silver font-body mb-1">Balance</div>
              <div className="text-green-400 font-bold font-heading text-sm flex items-center">
                <DollarSign className="h-3 w-3 mr-1" />
                {formatCurrency(member.balance)}
              </div>
            </div>
            <div className="bg-secondary/50 p-3 rounded-lg">
              <div className="text-xs text-silver font-body mb-1">Earnings</div>
              <div className="text-foreground font-bold font-heading text-sm flex items-center">
                <TrendingUp className="h-3 w-3 mr-1 text-green-500" />
                {formatCurrency(member.totalEarnings)}
              </div>
            </div>
            <div className="bg-secondary/50 p-3 rounded-lg">
              <div className="text-xs text-silver font-body mb-1">Referrals</div>
              <div className="text-foreground font-bold font-heading text-sm flex items-center">
                <Users className="h-3 w-3 mr-1 text-blue-500" />
                {member.referralCount}
              </div>
            </div>
            <div className="bg-secondary/50 p-3 rounded-lg">
              <div className="text-xs text-silver font-body mb-1">Active Days</div>
              <div className="text-green-500 font-bold font-heading text-sm flex items-center">
                <Clock className="h-3 w-3 mr-1" />
                {member.daysActive}
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="mb-4">
            <h4 className="text-muted-foreground font-semibold text-xs flex items-center mb-2 font-heading">
              <User className="h-3 w-3 mr-2" />
              Contact Information
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-silver font-body">Mobile:</span>
                <span className="text-xs text-foreground font-body flex items-center">
                  <Phone className="h-3 w-3 mr-1 text-primary" />
                  {member.mobile}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-silver font-body">Email:</span>
                <span className="text-xs text-foreground font-body flex items-center truncate max-w-[150px]">
                  <Mail className="h-3 w-3 mr-1 text-primary" />
                  {member.email}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-silver font-body">Country:</span>
                <span className="text-xs text-foreground font-body">{member.country}</span>
              </div>
            </div>
          </div>

          {/* Account Details */}
          <div className="mb-4">
            <h4 className="text-muted-foreground font-semibold text-xs flex items-center mb-2 font-heading">
              <CreditCard className="h-3 w-3 mr-2" />
              Account Details
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-xs text-silver font-body">Password:</span>
                <div className="flex items-center">
                  <span className="text-xs text-foreground font-mono mr-2">
                    {showPassword[member.id] ? member.password : '••••••••'}
                  </span>
                  <button
                    onClick={(e) => togglePasswordVisibility(member.id, e)}
                    className="text-xs text-primary hover:text-primary/80"
                  >
                    {showPassword[member.id] ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-silver font-body">Sponsor ID:</span>
                <span className="text-xs text-foreground font-body">{member.sponsorId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-silver font-body">Joining Date:</span>
                <span className="text-xs text-foreground font-body">{formatDate(member.joiningDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-silver font-body">Last Login:</span>
                <span className="text-xs text-foreground font-body">{formatDate(member.lastLogin)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-silver font-body">Status:</span>
                <span className="text-xs text-green-500 font-body">{member.status}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t border-border">
            <button
              onClick={() => handleViewProfile(member)}
              className="flex-1 px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors font-body flex items-center justify-center text-sm"
            >
              <Eye className="h-4 w-4 mr-2" />
              View Profile
            </button>
            <button
              onClick={() => handleEditUser(member)}
              className="flex-1 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors font-body flex items-center justify-center text-sm"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto pt-10 px-4">
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground text-sm font-body">Loading active members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto pt-10 px-4">
      {showProfileDialog && <ProfileDialog />}
      
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground font-heading flex items-center">
              <UserCheck className="h-6 w-6 mr-2 text-green-500" />
              Active Members
            </h1>
            <p className="text-muted-foreground mt-1 text-sm font-body">
              Showing {activeMembers.length} active out of {totalMembers} total members
            </p>
          </div>
          <button
            onClick={exportToCSV}
            disabled={filteredMembers.length === 0}
            className={`px-4 py-2 rounded-lg transition-all flex items-center text-sm border ${
              filteredMembers.length === 0
                ? 'bg-secondary/80 text-muted-foreground border-border cursor-not-allowed'
                : 'btn-electric'
            }`}
          >
            <Download className="h-3.5 w-3.5 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="card-glow bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center mr-3">
              <Users className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <div className="text-green-500 text-xs mb-1 font-body">Active Members</div>
              <div className="text-xl font-bold text-green-400 font-heading">{activeMembers.length}</div>
              <div className="text-green-600 text-xs font-body">of {totalMembers} total</div>
            </div>
          </div>
        </div>

        <div className="card-glow bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center mr-3">
              <TrendingUp className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <div className="text-purple-500 text-xs mb-1 font-body">Total Earnings</div>
              <div className="text-xl font-bold text-purple-400 font-heading">
                {formatCurrency(calculateTotalEarnings())}
              </div>
            </div>
          </div>
        </div>

        <div className="card-glow bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center mr-3">
              <Clock className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <div className="text-orange-500 text-xs mb-1 font-body">Today Logins</div>
              <div className="text-xl font-bold text-orange-400 font-heading">
                {calculateTodayLogins()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-glow bg-secondary/50 border border-border rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold text-foreground font-heading">Filter Members</h2>
        </div>

        {/* Desktop Filters */}
        <div className="hidden lg:grid grid-cols-4 gap-3 mb-4">
          <div>
            <label className="block text-muted-foreground mb-1 text-xs font-body">Member ID:</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="text"
                name="memberId"
                value={filters.memberId}
                onChange={handleFilterChange}
                placeholder="Search ID"
                className="w-full text-xs pl-7 pr-3 py-1.5 bg-secondary/80 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-body"
              />
            </div>
          </div>

          <div>
            <label className="block text-muted-foreground mb-1 text-xs font-body">Mobile:</label>
            <div className="relative">
              <Phone className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="text"
                name="mobile"
                value={filters.mobile}
                onChange={handleFilterChange}
                placeholder="Search mobile"
                className="w-full text-xs pl-7 pr-3 py-1.5 bg-secondary/80 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-body"
              />
            </div>
          </div>

          <div>
            <label className="block text-muted-foreground mb-1 text-xs font-body">From Date:</label>
            <div className="relative">
              <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="date"
                name="joiningDateFrom"
                value={filters.joiningDateFrom}
                onChange={handleFilterChange}
                className="w-full text-xs pl-7 pr-3 py-1.5 bg-secondary/80 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary font-body"
              />
            </div>
          </div>

          <div>
            <label className="block text-muted-foreground mb-1 text-xs font-body">To Date:</label>
            <div className="relative">
              <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="date"
                name="joiningDateTo"
                value={filters.joiningDateTo}
                onChange={handleFilterChange}
                className="w-full text-xs pl-7 pr-3 py-1.5 bg-secondary/80 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary font-body"
              />
            </div>
          </div>
        </div>

        {/* Mobile Filters */}
        <div className="lg:hidden grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-muted-foreground mb-1 text-xs font-body">Member ID:</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="text"
                name="memberId"
                value={filters.memberId}
                onChange={handleFilterChange}
                placeholder="Search ID"
                className="w-full text-xs pl-7 pr-3 py-1.5 bg-secondary/80 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-body"
              />
            </div>
          </div>

          <div>
            <label className="block text-muted-foreground mb-1 text-xs font-body">Mobile:</label>
            <div className="relative">
              <Phone className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="text"
                name="mobile"
                value={filters.mobile}
                onChange={handleFilterChange}
                placeholder="Search mobile"
                className="w-full text-xs pl-7 pr-3 py-1.5 bg-secondary/80 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-body"
              />
            </div>
          </div>

          <div className="col-span-2">
            <label className="block text-muted-foreground mb-1 text-xs font-body">Date Range:</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <input
                  type="date"
                  name="joiningDateFrom"
                  value={filters.joiningDateFrom}
                  onChange={handleFilterChange}
                  placeholder="From"
                  className="w-full text-xs pl-7 pr-2 py-1.5 bg-secondary/80 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary font-body"
                />
              </div>
              <div className="relative">
                <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <input
                  type="date"
                  name="joiningDateTo"
                  value={filters.joiningDateTo}
                  onChange={handleFilterChange}
                  placeholder="To"
                  className="w-full text-xs pl-7 pr-2 py-1.5 bg-secondary/80 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary font-body"
                />
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={() => setFilters({ memberId: '', mobile: '', joiningDateFrom: '', joiningDateTo: '' })}
          className="px-3 py-1.5 text-xs bg-secondary/80 hover:bg-secondary text-muted-foreground rounded-lg transition-colors border border-border flex items-center font-body"
        >
          <Filter className="h-3 w-3 mr-1" />
          Clear Filters
        </button>
      </div>

      {/* Desktop Table View - Without Actions Column */}
      <div className="hidden lg:block card-glow bg-secondary/50 border border-border rounded-lg overflow-hidden mb-8">
        <div className="p-4 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground font-heading flex items-center">
            <UserCheck className="h-4 w-4 mr-2 text-green-500" />
            Active Members ({filteredMembers.length})
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead className="bg-secondary/80">
              <tr className="text-xs">
                <th className="py-2 px-2 text-center text-muted-foreground font-medium w-12 font-heading">#</th>
                <th className="py-2 px-2 text-left text-muted-foreground font-medium w-24 font-heading">Sponsor ID</th>
                <th 
                  className="py-2 px-2 text-left text-muted-foreground font-medium cursor-pointer hover:text-foreground w-40 font-heading"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center">
                    Member Info
                    <SortIcon columnKey="name" />
                  </div>
                </th>
                <th className="py-2 px-2 text-left text-muted-foreground font-medium w-28 font-heading">Earnings</th>
                <th className="py-2 px-2 text-left text-muted-foreground font-medium w-32 font-heading">Contact</th>
                <th 
                  className="py-2 px-2 text-left text-muted-foreground font-medium cursor-pointer hover:text-foreground w-28 font-heading"
                  onClick={() => handleSort('joiningDate')}
                >
                  <div className="flex items-center">
                    Dates
                    <SortIcon columnKey="joiningDate" />
                  </div>
                </th>
                <th 
                  className="py-2 px-2 text-left text-muted-foreground font-medium cursor-pointer hover:text-foreground w-24 font-heading"
                  onClick={() => handleSort('balance')}
                >
                  <div className="flex items-center">
                    Balance
                    <SortIcon columnKey="balance" />
                  </div>
                </th>
                <th className="py-2 px-2 text-left text-muted-foreground font-medium w-16 font-heading">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {filteredMembers.length > 0 ? (
                filteredMembers.map((member, index) => (
                  <DesktopTableRow key={member.id} member={member} index={index} />
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="py-12 text-center">
                    <div className="text-muted-foreground text-4xl mb-3">👤</div>
                    <h3 className="text-sm font-semibold text-foreground mb-2 font-heading">
                      {activeMembers.length === 0 ? 'No Active Members' : 'No Results Found'}
                    </h3>
                    <p className="text-muted-foreground text-xs mb-4 max-w-sm mx-auto font-body">
                      {activeMembers.length === 0 
                        ? 'There are no users with status "active" in the database.' 
                        : 'Try adjusting your search filters or clear them to see all active members.'}
                    </p>
                    {activeMembers.length > 0 && (
                      <button
                        onClick={() => setFilters({ memberId: '', mobile: '', joiningDateFrom: '', joiningDateTo: '' })}
                        className="px-4 py-2 text-xs btn-electric rounded-lg font-body"
                      >
                        Clear Filters
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Accordion View */}
      <div className="lg:hidden">
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-foreground font-heading flex items-center">
            <UserCheck className="h-4 w-4 mr-2 text-green-500" />
            Active Members ({filteredMembers.length})
          </h2>
        </div>

        {filteredMembers.length > 0 ? (
          filteredMembers.map((member, index) => (
            <MobileAccordionItem key={member.id} member={member} index={index} />
          ))
        ) : (
          <div className="card-glow p-8 text-center">
            <div className="text-muted-foreground text-4xl mb-3">👤</div>
            <h3 className="text-sm font-semibold text-foreground mb-2 font-heading">
              {activeMembers.length === 0 ? 'No Active Members' : 'No Results Found'}
            </h3>
            <p className="text-muted-foreground text-xs mb-4 max-w-sm mx-auto font-body">
              {activeMembers.length === 0 
                ? 'There are no users with status "active" in the database.' 
                : 'Try adjusting your search filters or clear them to see all active members.'}
            </p>
            {activeMembers.length > 0 && (
              <button
                onClick={() => setFilters({ memberId: '', mobile: '', joiningDateFrom: '', joiningDateTo: '' })}
                className="px-4 py-2 text-xs btn-electric rounded-lg font-body"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveMember;
